import type { CallRoomCoords } from "@xop/dashboard-shared";
import { api } from "../lib/api.js";

export interface AuthCallResult { sessionId: number; meetingId: number; conferenceId: number; channels: string[]; iceServers: RTCIceServer[]; token: string }

/**
 * All call endpoints go through the shared dashboard `api()` wrapper rather than
 * a bare `fetch`, so they get:
 *   - the ABSOLUTE base (`${env.apiUrl}${path}`) — a relative `/api/...` resolves
 *     to `tauri://localhost` in the desktop app and never reaches the server
 *     (the "API base" desktop trap), and
 *   - the bearer token + 401→refresh→retry, identical to every other dashboard call.
 */

export const authCall = (callId: string, participantPin: string, displayName: string) =>
  api<AuthCallResult>(`/api/calls/${callId}/auth`, { method: 'POST', body: { participantPin, displayName } });

// Phase 2 — broker actions (ring / accept / decline / cancel / end)
export const ringCall = (recipientId: number, recipientName: string) =>
  api<{ callId: string; state: string }>('/api/calls/ring', { method: 'POST', body: { recipient_id: recipientId, recipient_name: recipientName } });
/** Phase 11 — start a GROUP call to several users at once (busy ones skipped). */
export const ringGroupCall = (recipients: ReadonlyArray<{ id: number; name: string }>) =>
  api<{ callId: string; state: string }>('/api/calls/ring-group', { method: 'POST', body: { recipients } });
/** Phase 11 — add another user to an ONGOING call (rings them in). */
export const inviteCall = (callId: string, recipientId: number, recipientName: string) =>
  api<{ ok: boolean }>(`/api/calls/${callId}/invite`, { method: 'POST', body: { recipient_id: recipientId, recipient_name: recipientName } });
/** Start an OUTBOUND call with no staff invitee — provisions a room up front and
 * joins us alone so external PSTN numbers can be dialled in (the launcher's
 * keypad / CRM-contact targets). Returns the room to join immediately. */
export const startSoloCall = () =>
  api<{ callId: string; room: CallRoomCoords }>('/api/calls/start-solo', { method: 'POST', body: {} });
export const acceptCall = (callId: string) =>
  api<{ room: CallRoomCoords }>(`/api/calls/${callId}/accept`, { method: 'POST', body: {} });
export const declineCall = (callId: string) =>
  api<{ ok: boolean }>(`/api/calls/${callId}/decline`, { method: 'POST', body: {} });
export const cancelCall = (callId: string) =>
  api<{ ok: boolean }>(`/api/calls/${callId}/cancel`, { method: 'POST', body: {} });
export const endCall = (callId: string, keepalive = false) =>
  api<{ ok: boolean }>(`/api/calls/${callId}/end`, { method: 'POST', body: {}, ...(keepalive ? { keepalive: true } : {}) });
/** Keep-alive ping — tells the broker we're still in the call so its staleness
 * sweep doesn't reap it. Sent on an interval while a call is active. */
export const heartbeatCall = (callId: string) =>
  api<{ ok: boolean }>(`/api/calls/${callId}/heartbeat`, { method: 'POST', body: {} });
/** Signal a screen-share start/stop to the broker so it can relay to the peer.
 * `sharerSessionId` is the conferweb sessionId of the sharer (from `/auth`). */
export const screenSignal = (callId: string, sharing: boolean, sharerSessionId: number) =>
  api<{ ok: boolean }>(`/api/calls/${callId}/screen`, { method: 'POST', body: { sharing, sharer_session_id: sharerSessionId } });
/** Signal a camera on/off to the broker so it can relay to the peer (mirrors screenSignal). */
export const cameraSignal = (callId: string, sharing: boolean, sharerSessionId: number) =>
  api<{ ok: boolean }>(`/api/calls/${callId}/camera`, { method: 'POST', body: { sharing, sharer_session_id: sharerSessionId } });
/** What C1's `webcast/dial_out` returned, echoed by the server for diagnosis.
 * `status:true` only means C1's voiced daemon ACK'd the command — NOT that a PSTN
 * leg was originated or the phone rang. See conferweb.ts /dial. */
export interface DialC1Reply { httpStatus: number; status?: boolean; type?: string; reason?: string; message?: string }

/** Dial an EXTERNAL phone number into the call's conference (C1 `webcast/dial_out`).
 * `token` is the caller's webcast session token (from `/auth`) — the server relays
 * it to C1 to prove conference membership; the API key stays server-side. The
 * dialed number joins as an audio participant, bridged to everyone in the room. */
export const dialCall = (callId: string, number: string, token: string) =>
  api<{ ok: boolean; c1?: DialC1Reply }>(`/api/calls/${callId}/dial`, { method: 'POST', body: { number, token } });

/** Report our conferweb SFU session id (from `/auth`) so the server adds it to
 * our roster entry — peers use it to open a per-sid camera view leg (group video). */
export const reportSid = (callId: string, sid: number) =>
  api<{ ok: boolean }>(`/api/calls/${callId}/report-sid`, { method: 'POST', body: { sid } });

/** Register a published camera/screen stream with C1's SFU (`streamer_controls`)
 * so it actually FORWARDS the stream to viewers — C1 ignores a raw publish leg
 * until this fires. `token` is our webcast session token (from `/auth`); the
 * server replays the matching session cookie. */
export const streamerControls = (
  callId: string,
  source: 'camera' | 'screen',
  state: 'start_sharing' | 'stop_sharing',
  sid: number,
  token: string,
  mediaCount = 0,
) =>
  api<{ ok: boolean }>(`/api/calls/${callId}/streamer-controls`, {
    method: 'POST',
    body: { source, state, sid, media_count: mediaCount, token },
  });

/** One C1 attendee with its SFU sid + live sharing flags. */
export interface CallAttendee { id: number; camera_started: boolean; screen_shared: boolean }

/** One conference voice port (from C1 `get_attendees` → `response.audioCalls`).
 * `isPhone` (web_media_stream:false) + `audioConnected` ⇒ an ANSWERED PSTN leg;
 * `state === 12` ⇒ still ringing. Drives consultation off-hook detection. */
export interface CallAudioPort {
  id: number;
  state: number;
  audioConnected: boolean;
  isPhone: boolean;
  numberDialed?: string;
}

/** Fetch C1's authoritative attendee list (`get_attendees`) so a joining client
 * can discover who is ALREADY publishing camera and subscribe — independent of
 * broker frame timing (fixes the late-joiner "doesn't see existing cameras").
 * Also returns `audioPorts` (voice-port state) for consultation answer-detection. */
export const fetchCallAttendees = (callId: string, token: string) =>
  api<{ attendees: CallAttendee[]; audioPorts?: CallAudioPort[] }>(`/api/calls/${callId}/attendees`, { method: 'POST', body: { token } });
